/* eslint-disable camelcase */
import React, { useEffect, useState } from 'react';
import { Modal, Form, Col, Row, Input, Select, Switch, DatePicker, InputNumber, Checkbox, Divider } from 'antd';
import moment from 'moment';
import { BILLING_CURRENCIES } from '../utils/constants';

const { Option } = Select;

const billingPeriods = ['monthly', 'quarterly', 'yearly'];

function OrganizationModal(props: any) {
	const { visible, onClose, onSave, saving, selectedOrg } = props;
	const [selectedBillingPeriod, setBillingPeriod] = useState('monthly');
	const [selectedTrialEndDate, setTrialEndDate] = useState(selectedOrg.billing?.trial_end || null);
	const [isFreeloader, toggleFreeloader] = useState(selectedOrg.billing?.is_freeloader || false);
	const [isInactive, toggleInactive] = useState(selectedOrg.billing?.is_inactive || false);


	const [form] = Form.useForm();

	useEffect(() => {
		if (selectedOrg.billing?.billing_period !== selectedBillingPeriod) {
			setBillingPeriod(selectedOrg.billing?.billing_period || 'monthly');
		}
	}, [selectedOrg.billing?.billing_period]);

	useEffect(() => {
		if (selectedOrg.billing?.trial_end !== selectedTrialEndDate) {
			setTrialEndDate(selectedOrg.billing?.trial_end || null);
		}
	}, [selectedOrg.billing?.trial_end]);

	function saveForm(values: any) {
		if (selectedOrg.id) {
			values.id = selectedOrg.id;
		}

		if (moment.isMoment(values.billing.trial_end)) {
			values.billing.trial_end = moment(values.billing.trial_end).format('YYYY-MM-DD');
		}

		values.billing.is_freeloader = isFreeloader;
		values.billing.is_inactive = isInactive;

		onSave({
			...values,
		});
	}

	useEffect(() => {
		form.resetFields();
		toggleFreeloader(selectedOrg.billing?.is_freeloader);
		toggleInactive(selectedOrg.billing?.is_inactive);
	}, [selectedOrg]);

	const selectedCurrency = selectedOrg.billing?.currency || 'eur';

	function getFirstPaymentDate() {
		if (selectedTrialEndDate) {
			const trialEndDate = selectedTrialEndDate ? moment(selectedTrialEndDate) : null;
			const billingPeriod = selectedBillingPeriod;
			let firstPaymentDate = null;

			if (billingPeriod === 'quarterly' || billingPeriod === 'yearly') {
				firstPaymentDate = trialEndDate ? trialEndDate.add(1, 'month').startOf('month') : null;
			}

			if (billingPeriod === 'monthly') {
				firstPaymentDate = trialEndDate ? trialEndDate.add(2, 'month').startOf('month') : null;
			}

			return firstPaymentDate ? `First payment date: ${firstPaymentDate.format('DD.MM.YYYY')}` : '';
		}

		return '';
	}

	return (
		<Modal
			okText="Save"
			cancelText="Cancel"
			title={selectedOrg.id ? 'Edit organization' : 'Add organization'}
			okButtonProps={{ loading: saving }}
			visible={visible}
			onOk={() => form.submit()}
			destroyOnClose={true}
			onCancel={onClose}
		>
			<Form form={form} id="orgForm" onFinish={saveForm} layout="vertical">
				<Row gutter={16}>
					<Col span={24}>
						<Form.Item
							name="name"
							label="Name"
							initialValue={selectedOrg.name || ''}
							rules={[{ required: true, message: 'Please insert organization name' }]}
						>
							<Input placeholder="Name" />
						</Form.Item>
					</Col>
					<Col span={24}>
						<Form.Item name="region" label="Region" initialValue={selectedOrg.region || 'default'}>
							<Select>
								<Select.Option key="default" value="default">
									Default
								</Select.Option>
								<Select.Option key="dgca" value="dgca">
									DGCA
								</Select.Option>
							</Select>
						</Form.Item>
						<Form.Item
							valuePropName="checked"
							name="hide_powered_by"
							label="Hide Powered By Wingops"
							initialValue={selectedOrg.hide_powered_by || false}
						>
							<Switch />
						</Form.Item>
					</Col>
					<Divider></Divider>
					<Col span={12}>
						<Form.Item valuePropName="checked">
							<Checkbox
								disabled={isInactive}
								checked={isFreeloader}
								onChange={(ev) => toggleFreeloader(ev.target.checked)}
							>
								Freeloader
							</Checkbox>
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item valuePropName="checked">
							<Checkbox
								disabled={isFreeloader}
								checked={isInactive}
								onChange={(ev) => toggleInactive(ev.target.checked)}
							>
								Inactive
							</Checkbox>
						</Form.Item>
					</Col>
					<Col span={24}>
						<Form.Item name={['billing', 'trial_end']} label="Trial End Date" extra={getFirstPaymentDate()}>
							<DatePicker
								disabled={isFreeloader || isInactive}
								style={{ width: '100%' }}
								onChange={(date) => setTrialEndDate(date ? date.format('YYYY-MM-DD') : null)}
								defaultValue={
									selectedOrg.billing?.trial_end ? moment(selectedOrg.billing?.trial_end) : undefined
								}
								format="DD.MM.YYYY"
							/>
						</Form.Item>
					</Col>
					<Col span={24}>
						<Form.Item name={['billing', 'currency']} label="Currency" initialValue={selectedCurrency}>
							<Select disabled={isFreeloader || isInactive} style={{ width: '100%' }}>
								{BILLING_CURRENCIES.map((currency) => {
									return (
										<Option key={currency.value} value={currency.value}>
											{currency.symbol}
										</Option>
									);
								})}
							</Select>
						</Form.Item>
					</Col>
					<Col span={24}>
						<Form.Item
							name={['billing', 'billing_period']}
							label="Billing period"
							initialValue={selectedOrg.billing?.billing_period || 'monthly'}
						>
							<Select
								disabled={isFreeloader || isInactive}
								onChange={setBillingPeriod}
								placeholder={'Billing Period'}
							>
								{billingPeriods.map((billingPeriod) => {
									return (
										<Option key={billingPeriod} value={billingPeriod}>
											{billingPeriod}
										</Option>
									);
								})}
							</Select>
						</Form.Item>
					</Col>
					<Col span={24}>
						<Form.Item
							name={['billing', 'base_fee']}
							label={`Base Fee (${selectedCurrency.toUpperCase()})`}
							initialValue={selectedOrg.billing ? selectedOrg.billing?.base_fee : 0}
						>
							<InputNumber
								disabled={isFreeloader || isInactive}
								style={{ width: '100%' }}
								min={0}
								placeholder="Base fee"
							/>
						</Form.Item>
					</Col>
					<Col span={24}>
						<Form.Item
							name={['billing', 'max_aircraft_count']}
							label={`Max. Aircraft Count`}
							initialValue={selectedOrg.billing?.max_aircraft_count || null}
						>
							<InputNumber
								disabled={isFreeloader || isInactive}
								style={{ width: '100%' }}
								min={0}
								placeholder="Max. Aircraft Count"
							/>
						</Form.Item>
					</Col>
					{selectedBillingPeriod === 'monthly' && [
						<Col key="no_engine" span={24}>
							<Form.Item
								name={['billing', 'per_aircraft_fee_no_engine']}
								label={`Aircraft Fee No Engine (${selectedCurrency.toUpperCase()})`}
								initialValue={
									selectedOrg.billing ? selectedOrg.billing?.per_aircraft_fee_no_engine : 10
								}
							>
								<InputNumber
									disabled={isFreeloader || isInactive}
									style={{ width: '100%' }}
									min={0}
									placeholder="Aircraft Fee No Engine"
								/>
							</Form.Item>
						</Col>,
						<Col key="piston" span={24}>
							<Form.Item
								key="piston"
								name={['billing', 'per_aircraft_fee_piston']}
								label={`Aircraft Fee Piston Engine (${selectedCurrency.toUpperCase()})`}
								initialValue={selectedOrg.billing ? selectedOrg.billing?.per_aircraft_fee_piston : 35}
							>
								<InputNumber
									disabled={isFreeloader || isInactive}
									style={{ width: '100%' }}
									min={0}
									placeholder="Aircraft Fee Piston Engine"
								/>
							</Form.Item>
						</Col>,
						<Col key="turbine" span={24}>
							<Form.Item
								key="turbine"
								name={['billing', 'per_aircraft_fee_turbine']}
								label={`Aircraft Fee Single Turbine (${selectedCurrency.toUpperCase()})`}
								initialValue={selectedOrg.billing ? selectedOrg.billing?.per_aircraft_fee_turbine : 80}
							>
								<InputNumber
									disabled={isFreeloader || isInactive}
									style={{ width: '100%' }}
									min={0}
									placeholder="Aircraft Fee Single Turbine"
								/>
							</Form.Item>
						</Col>,
						<Col key="twin_turbine" span={24}>
							<Form.Item
								key="twin_turbine"
								name={['billing', 'per_aircraft_fee_twin_turbine']}
								label={`Aircraft Fee Twin Turbine (${selectedCurrency.toUpperCase()})`}
								initialValue={
									selectedOrg.billing ? selectedOrg.billing?.per_aircraft_fee_twin_turbine : 150
								}
							>
								<InputNumber
									disabled={isFreeloader || isInactive}
									style={{ width: '100%' }}
									min={0}
									placeholder="Aircraft Fee Twin Turbine"
								/>
							</Form.Item>
						</Col>,
						<Col key="jet" span={24}>
							<Form.Item
								key="jet"
								name={['billing', 'per_aircraft_fee_jet']}
								label={`Aircraft Fee Jet (${selectedCurrency.toUpperCase()})`}
								initialValue={selectedOrg.billing ? selectedOrg.billing?.per_aircraft_fee_jet : 250}
							>
								<InputNumber
									disabled={isFreeloader || isInactive}
									style={{ width: '100%' }}
									min={0}
									placeholder="Aircraft Fee Jet"
								/>
							</Form.Item>
						</Col>,
					]}
				</Row>
			</Form>
		</Modal>
	);
}

export default OrganizationModal;
